import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, CheckCircle, Percent, Truck, Bell, ShieldCheck, ChevronRight, ChevronDown } from "lucide-react";

const ZONAS = ["Turén", "Acarigua", "El Playón", "Píritu", "Guanare"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegistroPublico() {
  const [catalogo, setCatalogo] = useState([]);
  const [catalogoCargando, setCatalogoCargando] = useState(true);
  const [catalogoError, setCatalogoError] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    nombre: "", apellidos: "", cedula: "", telefono: "",
    direccion: "", zona: "", email: "",
  });

  const [medicamentos, setMedicamentos] = useState([
    { medicamento_id: "", concentracion: "", dosis_diaria: "", marca_comercial: "" }
  ]);

  const cargarCatalogo = () => {
    setCatalogoCargando(true);
    setCatalogoError(false);
    axios.get(`${BACKEND_URL}/api/medicamentos-publico`)
      .then(({ data }) => { setCatalogo(data); setCatalogoCargando(false); })
      .catch(() => { setCatalogoCargando(false); setCatalogoError(true); });
  };

  useEffect(() => { cargarCatalogo(); }, []);

  const setF = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const updateMed = (idx, field, value) =>
    setMedicamentos(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));

  const addMed = () => setMedicamentos(prev => [...prev, { medicamento_id: "", concentracion: "", dosis_diaria: "", marca_comercial: "" }]);
  const removeMed = (idx) => setMedicamentos(prev => prev.filter((_, i) => i !== idx));

  const handleMedSelect = (idx, medicamento_id) => {
    const found = catalogo.find(c => c.id === medicamento_id);
    setMedicamentos(prev => prev.map((m, i) =>
      i === idx ? { ...m, medicamento_id, concentracion: found?.concentracion || "" } : m
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");


    const medsValidos = medicamentos.filter(m => m.medicamento_id && m.dosis_diaria);
    if (medsValidos.length === 0) {
      setError("Debes agregar al menos un medicamento con su dosis.");
      return;
    }
    const sinConcentracion = medsValidos.find(m => !m.concentracion.trim());
    if (sinConcentracion) {
      setError("La concentración del medicamento es obligatoria.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/registro-publico`, {
        ...form,
        zona: form.zona || null,
        email: form.email || null,
        medicamentos: medsValidos.map(m => ({
          medicamento_id: m.medicamento_id,
          concentracion: m.concentracion,
          dosis_diaria: parseFloat(m.dosis_diaria),
          marca_comercial: m.marca_comercial || null,
        })),
      });
      setEnviado(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Ocurrió un error. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  if (enviado) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 to-red-700 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-3">¡Registro exitoso!</h2>
          <p className="text-slate-500 mb-2">
            Bienvenido al programa <strong>Ahorra con Farmavida</strong>.
          </p>
          <p className="text-slate-500 text-sm">
            Cada vez que compres tu medicamento te avisaremos por WhatsApp cuando esté por acabarse
            para coordinarte el delivery con el <strong>10% de descuento</strong>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">

      {/* ===== HERO ===== */}
      <div className="relative bg-gradient-to-br from-red-900 via-red-800 to-red-700 text-white overflow-hidden">
        {/* Admin link — subtle, top right */}
        <div className="absolute top-4 right-4">
          <Link to="/login" className="text-white/20 text-xs hover:text-white/50 transition-colors">
            Administradores
          </Link>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <div className="inline-block bg-white/10 text-white/70 text-xs px-4 py-1.5 rounded-full mb-8 tracking-wide">
            Programa exclusivo para pacientes crónicos
          </div>

          <img
            src="/logo-farmavida2.jpg"
            alt="Farmavida"
            className="w-24 h-24 rounded-2xl mx-auto mb-6 shadow-lg object-cover"
          />

          <h1 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
            ¡Suscríbete y Ahorra<br />
            <span className="text-red-200">con Farmavida!</span>
          </h1>

          <p className="italic text-red-100/90 text-base md:text-lg max-w-2xl mx-auto mb-6 leading-relaxed">
            "En Farmavida entendemos que cuidar tu salud no debería ser una carga. Sabemos lo que significa depender de un medicamento cada día — la preocupación de que no esté disponible, el tiempo perdido buscándolo, el esfuerzo de ir a buscarlo. Por eso diseñamos este programa: para que tu único trabajo sea cuidarte, y el nuestro sea asegurarnos de que siempre tengas lo que necesitas, en tu casa, a tiempo y al mejor precio."
          </p>

          <div className="bg-white/10 rounded-xl px-6 py-4 max-w-xl mx-auto mb-8">
            <p className="text-white text-sm leading-relaxed">
              Garantiza la disponibilidad de tus medicamentos crónicos, recíbelos en tu casa sin costo adicional y ahorra un <strong>10% en cada compra</strong>.
            </p>
          </div>

          <a href="#registro">
            <Button size="lg" className="bg-white text-red-800 hover:bg-red-50 font-bold text-base px-8 shadow-lg">
              Registrarme ahora <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </a>

          <div className="mt-10 flex flex-col items-center gap-1 text-white/40 text-xs">
            <span>Conoce más</span>
            <ChevronDown className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* ===== BENEFICIOS ===== */}
      <div className="bg-slate-50 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-800 text-center mb-10">
            ¿Qué incluye el programa?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                Icon: Percent,
                color: "text-red-600",
                bg: "bg-red-50",
                title: "10% de descuento",
                desc: "En todos tus medicamentos crónicos registrados, en cada compra.",
              },
              {
                Icon: Bell,
                color: "text-blue-600",
                bg: "bg-blue-50",
                title: "Recordatorio por WhatsApp",
                desc: "Te avisamos antes de que se te acaben las pastillas para que no quedes sin.",
              },
              {
                Icon: Truck,
                color: "text-green-600",
                bg: "bg-green-50",
                title: "Delivery a domicilio",
                desc: "Te llevamos tus medicamentos directo a tu puerta cuando confirmes tu pedido.",
              },
              {
                Icon: ShieldCheck,
                color: "text-purple-600",
                bg: "bg-purple-50",
                title: "Disponibilidad garantizada",
                desc: "Reservamos tu medicamento para que siempre esté disponible cuando lo necesites.",
              },
            ].map(({ Icon, color, bg, title, desc }) => (
              <Card key={title} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                    <Icon className={`w-6 h-6 ${color}`} />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-2">{title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* ===== CÓMO FUNCIONA ===== */}
      <div className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-800 text-center mb-10">
            ¿Cómo funciona?
          </h2>
          <div className="space-y-6">
            {[
              {
                step: "1",
                title: "Te registras una sola vez",
                desc: "Completa el formulario con tus datos y los medicamentos crónicos que tomas. Solo toma unos minutos.",
              },
              {
                step: "2",
                title: "Compras normalmente en Farmavida",
                desc: "Cada vez que compres tu medicamento en cualquiera de nuestras sucursales, el sistema lo detecta automáticamente.",
              },
              {
                step: "3",
                title: "Recibees tu recordatorio y coordinamos el delivery",
                desc: "Días antes de que se te acaben, te enviamos un WhatsApp. Tú confirmas y te lo llevamos a tu casa con el 10% de descuento.",
              },
            ].map(({ step, title, desc }) => (
              <div key={step} className="flex gap-5 items-start">
                <div className="w-10 h-10 rounded-full bg-red-700 text-white font-bold text-lg flex items-center justify-center shrink-0">
                  {step}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== CTA BANNER ===== */}
      <div className="bg-gradient-to-r from-red-800 to-red-600 text-white py-12 px-6 text-center">
        <h2 className="text-2xl font-bold mb-3">
          ¡Únete hoy y empieza a ahorrar!
        </h2>
        <p className="text-red-100 mb-6 max-w-xl mx-auto">
          Es gratis, sin compromisos y puedes cancelar cuando quieras. Solo necesitas registrar tus medicamentos una vez.
        </p>
        <a href="#registro">
          <Button size="lg" className="bg-white text-red-800 hover:bg-red-50 font-bold px-8">
            Registrarme gratis <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        </a>
      </div>

      {/* ===== FORMULARIO DE REGISTRO ===== */}
      <div id="registro" className="py-16 px-6 bg-slate-50">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-slate-800">Completa tu registro</h2>
            <p className="text-slate-500 mt-2">Llena el formulario y en menos de 2 minutos estarás inscrito.</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Datos personales */}
              <div>
                <h3 className="font-semibold text-slate-700 mb-4 pb-2 border-b border-slate-100">Datos personales</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Nombre *</Label>
                      <Input className="mt-1" value={form.nombre} onChange={setF("nombre")} required />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Apellidos *</Label>
                      <Input className="mt-1" value={form.apellidos} onChange={setF("apellidos")} required />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Cédula *</Label>
                      <Input className="mt-1" placeholder="V-12345678" value={form.cedula} onChange={setF("cedula")} required />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Teléfono * <span className="text-slate-400 font-normal">(con +58)</span></Label>
                      <Input className="mt-1" placeholder="+584121234567" value={form.telefono} onChange={setF("telefono")} required />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Dirección para el delivery *</Label>
                    <Input className="mt-1" placeholder="Calle, edificio, piso, apartamento, sector..." value={form.direccion} onChange={setF("direccion")} required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Zona / Sucursal más cercana</Label>
                      <select
                        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-300"
                        value={form.zona}
                        onChange={setF("zona")}
                      >
                        <option value="">Selecciona tu zona</option>
                        {ZONAS.map(z => <option key={z} value={z}>{z}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Email <span className="text-slate-400 font-normal">(opcional)</span></Label>
                      <Input className="mt-1" type="email" value={form.email} onChange={setF("email")} />
                    </div>
                  </div>
                </div>
              </div>


              {/* Medicamentos */}
              <div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100 mb-4">
                  <h3 className="font-semibold text-slate-700">Mis medicamentos crónicos *</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addMed} className="text-red-700 border-red-200 hover:bg-red-50">
                    <Plus className="w-3 h-3 mr-1" /> Agregar
                  </Button>
                </div>

                {catalogoCargando && (
                  <p className="text-sm text-slate-400 mb-3">Cargando medicamentos...</p>
                )}
                {catalogoError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 flex items-center justify-between">
                    <p className="text-sm text-red-600">No se pudo cargar la lista de medicamentos.</p>
                    <button type="button" onClick={cargarCatalogo} className="text-sm text-red-700 font-semibold underline ml-3">
                      Reintentar
                    </button>
                  </div>
                )}

                <div className="space-y-3">
                  {medicamentos.map((m, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-xl p-4 space-y-3">
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">Medicamento *</Label>
                        <select
                          className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                          value={m.medicamento_id}
                          onChange={(e) => handleMedSelect(idx, e.target.value)}
                          required
                        >
                          <option value="">Selecciona tu medicamento</option>
                          {catalogo.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.nombre}{c.concentracion ? ` ${c.concentracion}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-3 items-end">
                        <div className="flex-1">
                          <Label className="text-xs text-slate-500 mb-1 block">Concentración * <span className="text-slate-400">(ej: 850mg, 10mg/5ml)</span></Label>
                          <Input
                            placeholder="Ej: 500mg"
                            value={m.concentracion}
                            onChange={(e) => updateMed(idx, "concentracion", e.target.value)}
                            required
                          />
                        </div>
                        <div className="w-40">
                          <Label className="text-xs text-slate-500 mb-1 block">Pastillas por día *</Label>
                          <Input
                            type="number" min="0.5" step="0.5"
                            placeholder="Ej: 2"
                            value={m.dosis_diaria}
                            onChange={(e) => updateMed(idx, "dosis_diaria", e.target.value)}
                            required
                          />
                        </div>
                        {medicamentos.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeMed(idx)}
                            className="text-red-400 hover:text-red-600 hover:bg-red-50 h-9 w-9 p-0 shrink-0">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">
                          Marca comercial <span className="text-slate-400">(opcional) · ej: Nefrotal, Coropres, Melif, Sitaglis</span>
                        </Label>
                        <Input
                          placeholder="¿Qué marca tomas? Ej: Coropres"
                          value={m.marca_comercial}
                          onChange={(e) => updateMed(idx, "marca_comercial", e.target.value)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-red-700 hover:bg-red-800 text-white h-12 text-base font-semibold rounded-xl shadow-md"
              >
                {loading ? "Registrando..." : "Inscribirme al programa"}
              </Button>

              <p className="text-xs text-center text-slate-400">
                Al registrarte aceptas recibir recordatorios por WhatsApp sobre tus medicamentos.
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-800 text-slate-400 text-center py-6 text-xs">
        © {new Date().getFullYear()} Farmavida · Programa Suscríbete y Ahorra
      </div>

    </div>
  );
}
